import { ref, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import websocketService from '@/services/websocket'

export function useWebSocket() {
  const store = useStore()
  const isConnected = ref(false)
  const connectionError = ref(null)
  const activeChannels = ref([])

  // Initialize WebSocket connection
  const connect = () => {
    try {
      const token = store.getters['auth/getAccessToken']
      if (!token) {
        console.warn('No auth token available for WebSocket connection')
        return false
      }

      websocketService.init(store, token)
      isConnected.value = websocketService.isConnected
      return true
    } catch (error) {
      console.error('Failed to connect WebSocket:', error)
      connectionError.value = error.message
      return false
    }
  }

  // Disconnect WebSocket
  const disconnect = () => {
    try {
      websocketService.disconnect()
      isConnected.value = false
      activeChannels.value = []
    } catch (error) {
      console.error('Failed to disconnect WebSocket:', error)
    }
  }

  // Listen to student session (for students)
  const listenToStudentSession = (pinCode) => {
    if (!websocketService.echo) {
      console.warn('WebSocket not available, skipping student session listener')
      return null
    }

    return websocketService.listenToStudentSession(pinCode, {
      onSessionStarted: (data) => {
        console.log('Session started via WebSocket, dispatching to store:', data)
        store.dispatch('session/onSessionStarted', data)
      },
      
      onSessionCompleted: (data) => {
        console.log('Session completed via WebSocket, dispatching to store:', data)
        store.dispatch('session/onSessionCompleted', data)
      },
      
      onSessionStatusUpdate: (data) => {
        console.log('Session status update via WebSocket, dispatching to store:', data)
        // Update session status based on received data
        if (data.session_status) {
          store.commit('session/SET_SESSION_STATUS', data.session_status)
        }
        if (data.waiting_count !== undefined) {
          store.commit('session/SET_WAITING_COUNT', data.waiting_count)
        }
        if (data.group_id) {
          store.commit('session/SET_GROUP_ID', data.group_id)
        }
      }
    })
  }

  // Listen to teacher session (for teachers)
  const listenToTeacherSession = (pinCode) => {
    if (!websocketService.echo) {
      console.warn('WebSocket not available, skipping teacher session listener')
      return null
    }

    return websocketService.listenToTeacherSession(pinCode, {
      onStudentJoined: (data) => {
        console.log('Student joined, dispatching to store')
        store.dispatch('session/onStudentJoined', data.participant)
      },
      
      onStudentLeft: (data) => {
        console.log('Student left, dispatching to store')
        store.dispatch('session/onStudentLeft', data.participant_id)
      },
      
      onStudentStatusUpdate: (data) => {
        console.log('Student status update, dispatching to store')
        store.commit('session/UPDATE_PARTICIPANT_STATUS', {
          participantId: data.participant_id,
          status: data.status
        })
      }
    })
  }

  // Leave a specific channel
  const leaveChannel = (channelName) => {
    websocketService.leaveChannel(channelName)
    activeChannels.value = websocketService.getConnectionStatus().activeChannels
  }

  // Leave all channels
  const leaveAllChannels = () => {
    websocketService.leaveAllChannels()
    activeChannels.value = []
  }

  // Get connection status
  const getConnectionStatus = () => {
    return websocketService.getConnectionStatus()
  }

  // Force reconnection
  const reconnect = () => {
    websocketService.reconnect()
  }

  // Update reactive state based on WebSocket service state
  const updateState = () => {
    const status = websocketService.getConnectionStatus()
    isConnected.value = status.isConnected
    activeChannels.value = status.activeChannels
  }

  // Lifecycle management
  onMounted(() => {
    // Auto-connect if token is available
    const token = store.getters['auth/getAccessToken']
    if (token) {
      connect()
    }
  })

  onUnmounted(() => {
    // Don't automatically disconnect on unmount as other components might be using it
    // disconnect()
  })

  return {
    // State
    isConnected,
    connectionError,
    activeChannels,
    
    // Methods
    connect,
    disconnect,
    listenToStudentSession,
    listenToTeacherSession,
    leaveChannel,
    leaveAllChannels,
    getConnectionStatus,
    reconnect,
    updateState
  }
}

export default useWebSocket